'use client';

import { useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';

import {
  buildGuestDetailsSchema,
  emptyGuest,
  guestRefusal,
  type GuestFormValues,
  type GuestRefusal,
} from '@/helpers/guest-pass';
import { getTermsAcceptanceKey } from '@/helpers/terms';
import { GENERIC_ERROR_MESSAGE } from '@/lib/constants';
import {
  getErrorCode,
  getErrorDetails,
  getErrorMessage,
} from '@/lib/error-utils';
import type { ResolvedTerms } from '@/services/terms';
import { TERMS_RESOLVE_QUERY_KEY } from '@/state/terms';

export const useGuestDetailsForm = ({
  hostEmail,
  termsDocs,
  defaultCountry,
  initialCount = 0,
}: {
  hostEmail?: string | null;
  termsDocs: ResolvedTerms[];
  defaultCountry: string;
  // Cards open on mount, for a form that only exists to take a guest.
  initialCount?: number;
}) => {
  const queryClient = useQueryClient();
  const termsKey = termsDocs.length ? getTermsAcceptanceKey(termsDocs) : null;
  const schema = useMemo(
    () => buildGuestDetailsSchema({ hostEmail, termsKey }),
    [hostEmail, termsKey]
  );
  const form = useForm<GuestFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      guests: Array.from({ length: initialCount }, () =>
        emptyGuest(defaultCountry)
      ),
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });
  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: 'guests',
  });
  const [shake, setShake] = useState(0);
  const [listError, setListError] = useState('');

  // One append for several cards: useFieldArray actions must not be stacked
  // in one render.
  const addGuests = (count: number) => {
    setListError('');
    append(Array.from({ length: count }, () => emptyGuest(defaultCountry)));
  };

  const addGuest = () => addGuests(1);

  const removeGuest = (index: number) => {
    setListError('');
    remove(index);
  };

  const clearGuests = () => {
    setListError('');
    replace([]);
  };

  // handleSubmit rather than trigger: only a submitted form re-validates each
  // field as it changes, so a fixed field drops its error at once.
  const validate = () =>
    new Promise<boolean>((resolve) => {
      setListError('');
      form.handleSubmit(
        () => resolve(true),
        () => {
          setShake((s) => s + 1);
          resolve(false);
        }
      )();
    });

  const applyServerError = (
    error: unknown,
    options?: { canRemoveGuests?: boolean }
  ): GuestRefusal | null => {
    const refusal = guestRefusal(
      getErrorCode(error),
      getErrorDetails(error),
      getErrorMessage(error, GENERIC_ERROR_MESSAGE),
      options
    );
    if (refusal?.kind === 'list') setListError(refusal.message);
    if (refusal?.kind === 'field') {
      const path = `guests.${refusal.index}.${refusal.field}` as const;
      if (refusal.field === 'terms_key') {
        form.setValue(path, null);
        queryClient.invalidateQueries({ queryKey: [TERMS_RESOLVE_QUERY_KEY] });
      }
      form.setError(path, { message: refusal.message }, { shouldFocus: true });
      if (refusal.alsoField) {
        form.setError(`guests.${refusal.index}.${refusal.alsoField}`, {
          message: '',
        });
      }
      setShake((s) => s + 1);
    }
    return refusal;
  };

  return {
    form,
    fields,
    termsKey,
    shake,
    listError,
    addGuest,
    addGuests,
    removeGuest,
    clearGuests,
    validate,
    applyServerError,
  };
};

export type GuestDetailsForm = ReturnType<typeof useGuestDetailsForm>;
